import { Line } from '@react-three/drei';
import { type ThreeEvent, useThree } from '@react-three/fiber';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DoubleSide, MeshBasicMaterial, Vector3 } from 'three';

import { LayerType } from 'app/modules/wallforming/components/scenes/camera-controls/types/layerType';
import { usePoiBatchedMeshHandler } from 'app/modules/wallforming/components/scenes/objects/pois/hooks/usePoiBatchedMeshHandler';
import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import { PoiGeometryType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiMeshType';
import { createPoiFaceRaycast } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiRaycastUtils';
import { createPoiGeometryByType } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import {
    INTERACTION_RENDER_ORDER,
    INTERACTION_LINE_WIDTH_PX,
    INTERACTION_HALO_WIDTH_PX
} from 'app/modules/wallforming/components/scenes/objects/utils/screenSpaceUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { PRIMARY_COLOR_ACCENT_HEX } from 'shared/constants';

import type { PoiBatchedEntry } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiBatchedEntry';

const geometryByType = createPoiGeometryByType(PoiGeometryType.FACES);

interface PoiFacesProps {
    entries: PoiBatchedEntry[];
    showVisuals?: boolean;
}

export const PoiFaces = ({ entries, showVisuals = true }: PoiFacesProps) => {
    const poiMaterial = useMemo(
        () =>
            new MeshBasicMaterial({
                side: DoubleSide,
                polygonOffset: true,
                polygonOffsetFactor: -1,
                polygonOffsetUnits: -4
            }),
        []
    );

    useEffect(() => () => poiMaterial.dispose(), [poiMaterial]);

    const size = useThree((state) => state.size);
    const hiddenTypes = usePlacementStore((state) => state.hiddenPOIFilterTypes);
    const [hover, setHover] = useState<{ entries: PoiBatchedEntry[]; index: number } | null>(null);
    const {
        handleMeshRef,
        handleOnPoiSelection,
        handleOnPointerMove,
        handleOnPointerLeave,
        instanceIdByEntryIndexRef,
        entryIndexByInstanceIdRef
    } = usePoiBatchedMeshHandler({
        entries,
        geometriesByType: geometryByType
    });

    const raycast = useMemo(
        () => createPoiFaceRaycast(entries, instanceIdByEntryIndexRef, size),
        [entries, size, instanceIdByEntryIndexRef]
    );
    
    // Hover is bound to its entries snapshot so a POI update never highlights a stale index.
    const hoveredEntry = hover?.entries === entries ? entries[hover.index] : undefined;
    const highlightPoints = useMemo(
        () =>
            hoveredEntry?.filterType === POIFilterType.Corner && !hiddenTypes.includes(POIFilterType.Corner)
                ? [
                      new Vector3(0, -0.5, 0).applyMatrix4(hoveredEntry.matrix),
                      new Vector3(0, 0.5, 0).applyMatrix4(hoveredEntry.matrix)
                  ]
                : null,
        [hoveredEntry, hiddenTypes]
    );

    const handlePointerLeave = useCallback(
        (event: ThreeEvent<PointerEvent>) => {
            handleOnPointerLeave(event);
            setHover(null);
        },
        [handleOnPointerLeave]
    );

    const handlePointerMove = useCallback(
        (event: ThreeEvent<PointerEvent>) => {
            handleOnPointerMove(event);
            if (event.buttons !== 0) {
                return;
            }
            const index =
                event.batchId !== undefined ? entryIndexByInstanceIdRef.current.get(event.batchId) : undefined;
            setHover((previous) =>
                index === undefined
                    ? null
                    : previous?.entries === entries && previous.index === index
                      ? previous
                      : { entries, index }
            );
        },
        [entries, entryIndexByInstanceIdRef, handleOnPointerMove]
    );

    if (entries.length === 0) {
        return null;
    }

    return (
        <>
            <batchedMesh
                ref={handleMeshRef}
                args={[40_000, 40_000, 40_000, poiMaterial]}
                layers={[LayerType.RAYCAST]}
                material-visible={showVisuals}
                name="poi-interaction-target"
                perObjectFrustumCulled={true}
                raycast={raycast}
                renderOrder={100}
                onPointerDown={handleOnPoiSelection}
                onPointerLeave={handlePointerLeave}
                onPointerMove={handlePointerMove}
            />
            {showVisuals && highlightPoints && (
                <group name="poi-corner-highlight">
                    <Line
                        color="white"
                        depthTest={false}
                        depthWrite={false}
                        lineWidth={INTERACTION_HALO_WIDTH_PX}
                        points={highlightPoints}
                        renderOrder={INTERACTION_RENDER_ORDER}
                    />
                    <Line
                        color={PRIMARY_COLOR_ACCENT_HEX}
                        depthTest={false}
                        depthWrite={false}
                        lineWidth={INTERACTION_LINE_WIDTH_PX}
                        points={highlightPoints}
                        renderOrder={INTERACTION_RENDER_ORDER + 1}
                    />
                </group>
            )}
        </>
    );
};
